import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { SearchResult } from '../../hooks/useCommandPalette';
import { RecentItem } from '../../hooks/useRecentItems';

type Entity = RecentItem | SearchResult;

export function useEntityActions() {
  const navigate = useNavigate();

  const getEntityPath = (entity: Entity): string => {
    switch (entity.type) {
      case 'account':
        return `/firm/crm-accounts/${entity.id}`;
      case 'contact':
        return `/firm/crm-contacts/${entity.id}`;
      case 'opportunity':
        return `/investments/direct/${entity.id}/overview`;
      case 'view':
        // Navigate to the appropriate page with the view ID
        if (entity.metadata?.targetModel === 'account') {
          return `/firm/crm-accounts?viewId=${entity.id}`;
        } else {
          return `/firm/crm-contacts?viewId=${entity.id}`;
        }
      default:
        return '/';
    }
  };

  const viewEntity = (entity: Entity) => {
    const path = getEntityPath(entity);
    navigate(path);
  };

  const editEntity = (entity: Entity) => {
    // For now, navigate to the entity page where editing can be done
    // In the future, this could open an edit dialog
    const path = getEntityPath(entity);
    navigate(path);
    toast.info('Edit functionality coming soon');
  };

  const deleteEntity = (entity: Entity) => {
    // This would show a confirmation dialog and execute a delete mutation
    toast.error('Delete functionality coming soon');
  };

  const emailContact = (entity: Entity) => {
    if (entity.type === 'contact' && entity.metadata?.email) {
      window.open(`mailto:${entity.metadata.email}`, '_blank');
    } else {
      toast.error('No email address available');
    }
  };

  const callContact = (entity: Entity) => {
    if (entity.type === 'contact' && entity.metadata?.phone) {
      window.open(`tel:${entity.metadata.phone}`, '_blank');
    } else {
      toast.error('No phone number available');
    }
  };

  const viewRelatedAccounts = (entity: Entity) => {
    if (entity.type === 'contact') {
      navigate('/firm/crm-accounts');
      toast.info('Viewing related accounts');
    }
  };

  const viewRelatedContacts = (entity: Entity) => {
    if (entity.type === 'account') {
      navigate('/firm/crm-contacts');
      toast.info('Viewing related contacts');
    }
  };

  const createOpportunity = (entity: Entity) => {
    if (entity.type === 'account') {
      navigate('/investments/opportunities/new');
      toast.info('Create opportunity from account coming soon');
    }
  };

  const changeOpportunityStage = (entity: Entity) => {
    if (entity.type === 'opportunity') {
      toast.info('Change stage functionality coming soon');
    }
  };

  const addToIC = (entity: Entity) => {
    if (entity.type === 'opportunity') {
      toast.info('Add to IC functionality coming soon');
    }
  };

  return {
    addToIC,
    callContact,
    changeOpportunityStage,
    createOpportunity,
    deleteEntity,
    editEntity,
    emailContact,
    viewEntity,
    viewRelatedAccounts,
    viewRelatedContacts
  };
}
